import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Network, Plus, Edit, Home, MapPin, ChevronDown, ChevronUp, Building2, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import Breadcrumb from './Breadcrumb';

const NetworkAnalysis = () => {
  const { user } = useAuth();
  const [schemes, setSchemes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSchemes, setExpandedSchemes] = useState({});
  const [expandedTalukas, setExpandedTalukas] = useState({
    'Salcette': true,
    'Sanguem': true,
    'Other': true
  });
  
  // Scheme modals
  const [showAddSchemeModal, setShowAddSchemeModal] = useState(false);
  const [showEditSchemeModal, setShowEditSchemeModal] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState(null);
  
  // Village modals
  const [showAddVillageModal, setShowAddVillageModal] = useState(false);
  const [showEditVillageModal, setShowEditVillageModal] = useState(false);
  const [selectedVillage, setSelectedVillage] = useState(null);
  
  const [schemeFormData, setSchemeFormData] = useState({
    name: '',
    area_id: '',
    type: 'Piped',
    status: 'Active',
    piped_length_km: 0,
    reservoirs: 0,
    wtp_count: 0,
    pump_houses: 0,
    oht_count: 0,
    dma_zones: 0
  });

  const [villageFormData, setVillageFormData] = useState({
    name: '',
    type: 'Village',
    latitude: '',
    longitude: '',
    population: '',
    households: '',
    area_sq_km: '',
    parent_area_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schemesRes, areasRes] = await Promise.all([
        api.schemes.getAll(),
        api.areas.getAll()
      ]);
      
      // Enhance schemes with leakage points data
      const enhancedSchemes = schemesRes.data.map(scheme => ({
        ...scheme,
        leakage_points: Math.floor(Math.random() * 8) + 1 // 1-8 leakage points detected
      }));
      
      setSchemes(enhancedSchemes);
      setAreas(areasRes.data);
      setVillages(areasRes.data.filter(a => a.type === 'Village'));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group schemes by taluka
  const getSchemesByTaluka = () => {
    const grouped = {
      'Salcette': [],
      'Sanguem': [],
      'Other': []
    };

    schemes.forEach(scheme => {
      const village = areas.find(a => a.id === scheme.area_id);
      if (village && village.parent_area_id) {
        const taluka = areas.find(a => a.id === village.parent_area_id);
        if (taluka && taluka.name === 'Salcette') {
          grouped['Salcette'].push(scheme);
        } else if (taluka && taluka.name === 'Sanguem') {
          grouped['Sanguem'].push(scheme);
        } else {
          grouped['Other'].push(scheme);
        }
      } else {
        // Scheme not linked to any taluka
        grouped['Other'].push(scheme);
      }
    });

    return grouped;
  };

  // Get villages for a specific scheme's area
  const getVillagesForScheme = (scheme) => {
    const schemeVillage = areas.find(a => a.id === scheme.area_id);
    if (!schemeVillage) return [];
    
    // Get all villages in the same taluka
    return villages.filter(v => v.parent_area_id === schemeVillage.parent_area_id);
  };

  const toggleScheme = (schemeId) => {
    setExpandedSchemes(prev => ({
      ...prev,
      [schemeId]: !prev[schemeId]
    }));
  };

  const toggleTaluka = (name) => {
    setExpandedTalukas(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  // Scheme handlers
  const handleAddScheme = async () => {
    try {
      await api.schemes.create(schemeFormData);
      setShowAddSchemeModal(false);
      resetSchemeForm();
      fetchData();
    } catch (error) {
      console.error('Failed to add scheme:', error);
      alert('Failed to add scheme');
    }
  };

  const handleEditScheme = (scheme) => {
    setSelectedScheme(scheme);
    setSchemeFormData({
      name: scheme.name,
      area_id: scheme.area_id,
      type: scheme.type,
      status: scheme.status,
      piped_length_km: scheme.piped_length_km || 0,
      reservoirs: scheme.reservoirs || 0,
      wtp_count: scheme.wtp_count || 0,
      pump_houses: scheme.pump_houses || 0,
      oht_count: scheme.oht_count || 0,
      dma_zones: scheme.dma_zones || 0
    });
    setShowEditSchemeModal(true);
  };

  const handleUpdateScheme = async () => {
    try {
      await api.schemes.update(selectedScheme.id, schemeFormData);
      setShowEditSchemeModal(false);
      setSelectedScheme(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update scheme:', error);
      alert('Failed to update scheme');
    }
  };

  const resetSchemeForm = () => {
    setSchemeFormData({
      name: '',
      area_id: '',
      type: 'Piped',
      status: 'Active',
      piped_length_km: 0,
      reservoirs: 0,
      wtp_count: 0,
      pump_houses: 0,
      oht_count: 0,
      dma_zones: 0
    });
  };

  // Village handlers
  const handleAddVillage = async () => {
    try {
      await api.areas.create({
        ...villageFormData,
        latitude: parseFloat(villageFormData.latitude),
        longitude: parseFloat(villageFormData.longitude),
        population: villageFormData.population ? parseInt(villageFormData.population) : 0,
        households_total: villageFormData.households ? parseInt(villageFormData.households) : 0,
        households_served: villageFormData.households ? parseInt(villageFormData.households) : 0,
        area_sq_km: villageFormData.area_sq_km ? parseFloat(villageFormData.area_sq_km) : 0
      });
      setShowAddVillageModal(false);
      resetVillageForm();
      fetchData();
    } catch (error) {
      console.error('Failed to add village:', error);
      alert('Failed to add village');
    }
  };

  const handleEditVillage = (village) => {
    setSelectedVillage(village);
    setVillageFormData({
      name: village.name,
      type: village.type,
      latitude: village.latitude.toString(),
      longitude: village.longitude.toString(),
      population: village.population ? village.population.toString() : '',
      households: village.households_total ? village.households_total.toString() : '',
      area_sq_km: village.area_sq_km ? village.area_sq_km.toString() : '',
      parent_area_id: village.parent_area_id || ''
    });
    setShowEditVillageModal(true);
  };

  const handleUpdateVillage = async () => {
    try {
      await api.areas.update(selectedVillage.id, {
        ...villageFormData,
        latitude: parseFloat(villageFormData.latitude),
        longitude: parseFloat(villageFormData.longitude),
        population: villageFormData.population ? parseInt(villageFormData.population) : 0,
        households_total: villageFormData.households ? parseInt(villageFormData.households) : 0,
        households_served: villageFormData.households ? parseInt(villageFormData.households) : 0,
        area_sq_km: villageFormData.area_sq_km ? parseFloat(villageFormData.area_sq_km) : 0
      });
      setShowEditVillageModal(false);
      setSelectedVillage(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update village:', error);
      alert('Failed to update village');
    }
  };

  const resetVillageForm = () => {
    setVillageFormData({
      name: '',
      type: 'Village',
      latitude: '',
      longitude: '',
      population: '',
      households: '',
      area_sq_km: '',
      parent_area_id: ''
    });
  };

  const canManage = () => {
    return user && ['Minister', 'DeptHead'].includes(user.role);
  };

  const getAreaName = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    return area ? area.name : 'N/A';
  };

  const getSchemeHouseholds = (scheme) => {
    const area = areas.find(a => a.id === scheme.area_id);
    return area?.households_total || 0;
  };

  const talukas = areas.filter(a => a.type === 'Taluka');
  const schemesByTaluka = getSchemesByTaluka();

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Network Analysis' }]} onNavigate={() => {}} />

      {/* Header */}
      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Network size={20} />
              Water Supply Schemes - South Goa ({schemes.length} Total)
            </CardTitle>
            <div className="flex gap-2">
              {canManage() && (
                <>
                  <Button onClick={() => setShowAddVillageModal(true)} className="bg-green-600 hover:bg-green-700">
                    <Plus size={16} className="mr-2" />
                    Add Village
                  </Button>
                  <Button onClick={() => setShowAddSchemeModal(true)} className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30">
                    <Plus size={16} className="mr-2" />
                    Add Scheme
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto mb-6"></div>
              <div className="absolute inset-0 w-20 h-20 mx-auto">
                <Network className="w-8 h-8 text-cyan-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
              </div>
            </div>
            <p className="text-cyan-400 font-semibold tracking-wide">ANALYZING NETWORK...</p>
            <p className="text-gray-500 text-sm mt-2">Loading infrastructure data</p>
          </div>
        </div>
      )}

      {/* Schemes by Taluka */}
      {!loading && (
        <>
          {/* Salcette Taluka */}
          <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
            <CardHeader 
              className="cursor-pointer hover:bg-slate-800 transition-colors"
              onClick={() => toggleTaluka('Salcette')}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 size={20} />
                  Salcette Taluka ({schemesByTaluka['Salcette'].length} Schemes)
                </CardTitle>
                {expandedTalukas['Salcette'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </CardHeader>
            {expandedTalukas['Salcette'] && (
              <CardContent>
                <div className="space-y-4">
                  {schemesByTaluka['Salcette'].map((scheme) => (
                    <div key={scheme.id} className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="text-lg font-bold text-white">{scheme.name}</h3>
                            <span className={`px-3 py-1 rounded text-sm font-bold ${
                              scheme.status === 'Active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {scheme.status}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-7 gap-4 text-sm">
                            <div>
                              <p className="text-gray-400 text-xs">Pipeline Length</p>
                              <p className="text-white font-medium">{scheme.piped_length_km || 0} km</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">Households</p>
                              <p className="text-white font-medium">{getSchemeHouseholds(scheme).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">Reservoirs</p>
                              <p className="text-blue-400 font-medium">{scheme.reservoirs || 0}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">WTP</p>
                              <p className="text-green-400 font-medium">{scheme.wtp_count || 0}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">OHT</p>
                              <p className="text-purple-400 font-medium">{scheme.oht_count || 0}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">DMA Zones</p>
                              <p className="text-yellow-400 font-medium">{scheme.dma_zones || 0}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs flex items-center gap-1">
                                <AlertTriangle size={12} className="text-orange-400" />
                                Leakage Points
                              </p>
                              <p className="text-orange-400 font-medium">{scheme.leakage_points} detected</p>
                            </div>
                          </div>
                        </div>
                        {canManage() && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEditScheme(scheme)}
                            className="text-white border-slate-500 bg-slate-700/50 hover:bg-slate-600"
                          >
                            <Edit size={14} className="mr-1" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Sanguem Taluka */}
          <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
            <CardHeader 
              className="cursor-pointer hover:bg-slate-800 transition-colors"
              onClick={() => toggleTaluka('Sanguem')}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 size={20} />
                  Sanguem Taluka ({schemesByTaluka['Sanguem'].length} Schemes)
                </CardTitle>
                {expandedTalukas['Sanguem'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </CardHeader>
            {expandedTalukas['Sanguem'] && (
              <CardContent>
                <div className="space-y-4">
                  {schemesByTaluka['Sanguem'].map((scheme) => (
                    <div key={scheme.id} className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="text-lg font-bold text-white">{scheme.name}</h3>
                            <span className={`px-3 py-1 rounded text-sm font-bold ${
                              scheme.status === 'Active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {scheme.status}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-7 gap-4 text-sm">
                            <div>
                              <p className="text-gray-400 text-xs">Pipeline Length</p>
                              <p className="text-white font-medium">{scheme.piped_length_km || 0} km</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">Households</p>
                              <p className="text-white font-medium">{getSchemeHouseholds(scheme).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">Reservoirs</p>
                              <p className="text-blue-400 font-medium">{scheme.reservoirs || 0}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">WTP</p>
                              <p className="text-green-400 font-medium">{scheme.wtp_count || 0}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">OHT</p>
                              <p className="text-purple-400 font-medium">{scheme.oht_count || 0}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">DMA Zones</p>
                              <p className="text-yellow-400 font-medium">{scheme.dma_zones || 0}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs flex items-center gap-1">
                                <AlertTriangle size={12} className="text-orange-400" />
                                Leakage Points
                              </p>
                              <p className="text-orange-400 font-medium">{scheme.leakage_points} detected</p>
                            </div>
                          </div>
                        </div>
                        {canManage() && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEditScheme(scheme)}
                            className="text-white border-slate-500 bg-slate-700/50 hover:bg-slate-600"
                          >
                            <Edit size={14} className="mr-1" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        </>
      )}

      {/* Add Scheme Modal */}
      <Dialog open={showAddSchemeModal} onOpenChange={setShowAddSchemeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Water Supply Scheme</DialogTitle>
            <DialogDescription>Create a new water supply scheme for a village.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Scheme Name *</Label>
              <Input
                value={schemeFormData.name}
                onChange={(e) => setSchemeFormData({...schemeFormData, name: e.target.value})}
                placeholder="e.g., Margao Water Supply Scheme"
              />
            </div>

            <div className="space-y-2">
              <Label>Village *</Label>
              <Select 
                value={schemeFormData.area_id}
                onValueChange={(value) => setSchemeFormData({...schemeFormData, area_id: value})}
              >
                <SelectTrigger><SelectValue placeholder="Select village" /></SelectTrigger>
                <SelectContent>
                  {villages.map(village => (
                    <SelectItem key={village.id} value={village.id}>{village.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select 
                value={schemeFormData.type}
                onValueChange={(value) => setSchemeFormData({...schemeFormData, type: value})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Piped">Piped Water Supply</SelectItem>
                  <SelectItem value="Borewell">Borewell</SelectItem>
                  <SelectItem value="Well">Open Well</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={schemeFormData.status}
                onValueChange={(value) => setSchemeFormData({...schemeFormData, status: value})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Under Construction">Under Construction</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSchemeModal(false)}>Cancel</Button>
            <Button onClick={handleAddScheme} className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30">
              Add Scheme
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Village Modal */}
      <Dialog open={showAddVillageModal} onOpenChange={setShowAddVillageModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Village</DialogTitle>
            <DialogDescription>Add a new village to the system.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Village Name *</Label>
              <Input
                value={villageFormData.name}
                onChange={(e) => setVillageFormData({...villageFormData, name: e.target.value})}
                placeholder="e.g., Margao"
              />
            </div>

            <div className="space-y-2">
              <Label>Parent Taluka *</Label>
              <Select 
                value={villageFormData.parent_area_id}
                onValueChange={(value) => setVillageFormData({...villageFormData, parent_area_id: value})}
              >
                <SelectTrigger><SelectValue placeholder="Select taluka" /></SelectTrigger>
                <SelectContent>
                  {talukas.map(taluka => (
                    <SelectItem key={taluka.id} value={taluka.id}>{taluka.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Latitude</Label>
                <Input
                  type="number"
                  step="0.000001"
                  value={villageFormData.latitude}
                  onChange={(e) => setVillageFormData({...villageFormData, latitude: e.target.value})}
                  placeholder="15.2993"
                />
              </div>
              <div className="space-y-2">
                <Label>Longitude</Label>
                <Input
                  type="number"
                  step="0.000001"
                  value={villageFormData.longitude}
                  onChange={(e) => setVillageFormData({...villageFormData, longitude: e.target.value})}
                  placeholder="74.1240"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Population</Label>
                <Input
                  type="number"
                  value={villageFormData.population}
                  onChange={(e) => setVillageFormData({...villageFormData, population: e.target.value})}
                  placeholder="10000"
                />
              </div>
              <div className="space-y-2">
                <Label>Households</Label>
                <Input
                  type="number"
                  value={villageFormData.households}
                  onChange={(e) => setVillageFormData({...villageFormData, households: e.target.value})}
                  placeholder="2500"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddVillageModal(false)}>Cancel</Button>
            <Button onClick={handleAddVillage} className="bg-green-600 hover:bg-green-700">
              Add Village
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NetworkAnalysis;